import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useLocation, Link, useNavigate } from 'react-router-dom'
import { useGetDiscussionsQuery, useGetForYouDiscussionsQuery } from '../store/api/discussionsApi'
import { useGetCategoriesQuery } from '../store/api/categoriesApi'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useLoginPrompt } from '../context/LoginPromptContext'
import DiscussionCard from '../components/DiscussionCard'
import { MultiSelectDropdown } from '../components/ui/MultiSelectDropdown'
import { Button, Alert, Badge, EmptyState, Spinner, RefreshButton, FilterChip, SentinelLoader, PillTabs } from '../components/ui'
import { DeveloperBanner } from '../features/discussions/DeveloperBanner'
import s from '../styles/pages/HomePage.module.css'
import { DOM_EVENTS, DISCUSSIONS_PAGE_SIZE, PINNING_ENABLED } from '../lib/config'
import { contentPreview } from '../lib/avatar'

// ── Developer banner ────────────────────────────────────────────
const SORT_TABS = [
  { key: 'foryou',  label: '✨ For You', authOnly: true },
  { key: 'new',     label: '🕐 Latest' },
  { key: 'hot',     label: '🔥 Hot' },
  // { key: 'top',     label: '⬆️ Top' },
  { key: 'pinned',  label: '📌 Pinned', authOnly: true },
]

const SkeletonCard = () => (
  <div className={s.skeletonCard}>
    {[80, 60, 100, 40].map((w, i) => (
      <div key={i} className={s.skeletonLine} style={{ height: i === 0 ? '20px' : '14px', width: `${w}%` }} />
    ))}
  </div>
)

function MobileSearch() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (q.trim()) void navigate(`/search?q=${encodeURIComponent(q.trim())}`)
  }
  return (
    <form onSubmit={handleSubmit} className={s.mobileSearch}>
      <span style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.85rem', pointerEvents: 'none' }}>🔍</span>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search discussions..."
        style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.4rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
      />
    </form>
  )
}

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated, user } = useAppSelector((s) => s.auth)
  const [activeSort, setActiveSort] = useState(isAuthenticated ? 'foryou' : 'new')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const { trigger: promptLogin } = useLoginPrompt()
  const loaderRef = useRef<HTMLDivElement>(null)

  const rawCategories = searchParams.get('categorySlugs')
  const categorySlugs = rawCategories
    ? rawCategories.split(',').filter(Boolean)
    : searchParams.get('category') ? [searchParams.get('category')!] : []
  const tagFilter   = searchParams.get('tag') ?? undefined
  const searchQuery = searchParams.get('search') ?? undefined

  const { data: categoriesData } = useGetCategoriesQuery()
  const allCategories = categoriesData?.categories ?? []

  const handleCategoryChange = (slugs: string[]) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('categorySlugs')
      next.delete('category')
      if (slugs.length > 0) next.set('categorySlugs', slugs.join(','))
      next.delete('tag')
      return next
    }, { replace: true })
    setCursor(undefined)
  }

  const isForYou = activeSort === 'foryou'

  const { data: forYouData, isLoading: forYouLoading, isFetching: forYouFetching, refetch: refetchForYou } =
    useGetForYouDiscussionsQuery(
      {
        cursor,
        limit: DISCUSSIONS_PAGE_SIZE,
        ...(categorySlugs.length > 0 && { categorySlugs: categorySlugs.join(',') }),
      },
      { skip: !isAuthenticated || !isForYou }
    )

  const location = useLocation()
  const [removedBanner, setRemovedBanner] = useState<{ title: string | null } | null>(
    (location.state as any)?.removed ? { title: (location.state as any)?.removedTitle ?? null } : null
  )

  useEffect(() => {
    if ((location.state as any)?.removed) window.history.replaceState({}, '')
  }, [])

  useEffect(() => { setCursor(undefined) }, [activeSort, categorySlugs.join(','), tagFilter, searchQuery])

  const { data, isLoading, isFetching, refetch } = useGetDiscussionsQuery({
    sort: activeSort,
    ...(categorySlugs.length === 1 && { categorySlug: categorySlugs[0] }),
    ...(categorySlugs.length > 1  && { categorySlugs: categorySlugs.join(',') }),
    ...(tagFilter   && { tag: tagFilter }),
    ...(searchQuery && { search: searchQuery }),
    cursor,
    limit: DISCUSSIONS_PAGE_SIZE,
  }, { skip: isForYou })

  const activeData     = isForYou ? forYouData     : data
  const activeLoading  = isForYou ? forYouLoading  : isLoading
  const activeFetching = isForYou ? forYouFetching : isFetching
  const activeRefetch  = isForYou ? refetchForYou  : refetch
  const discussions    = activeData?.data ?? []

  useEffect(() => {
    if (!loaderRef.current || !activeData?.hasMore || activeFetching) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && activeData?.hasMore && !activeFetching) {
          if (!isAuthenticated) window.dispatchEvent(new Event(DOM_EVENTS.guestInteraction))
          setCursor(activeData.nextCursor ?? undefined)
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(loaderRef.current)
    return () => observer.disconnect()
  }, [activeData, activeFetching, isAuthenticated])

  return (
    <div className={s.page} style={{ cursor: activeFetching ? 'wait' : 'default' }}>

      {/* Removed content banner */}
      {removedBanner && (
        <Alert variant="error" onDismiss={() => setRemovedBanner(null)} style={{ marginBottom: '1rem', flexShrink: 0 }}>
          The discussion{removedBanner.title
            ? <> <strong>"{removedBanner.title}"</strong></>
            : ''} was removed by a moderator.
        </Alert>
      )}

      {/* Mobile-only search bar */}
      <MobileSearch />

      <div className={s.sortHeader}>
        <div className={s.sortHeaderRow}>
          <div className={s.titleRow}>
            <h2 className={s.pageTitle}>
              {searchQuery
                ? `🔍 Results for "${searchQuery}"`
                : tagFilter ? `#${tagFilter}`
                : 'Discussion Threads'}
            </h2>
            <MultiSelectDropdown
              options={allCategories.map(c => ({ value: c.slug, label: c.name, icon: c.icon ?? undefined, color: c.color ?? undefined }))}
              selected={categorySlugs}
              onChange={handleCategoryChange}
              placeholder="Category"
              searchPlaceholder="Search categories..."
              align="left"
            />
            <Link to="/new" className={s.newDiscBtn}>➕ New</Link>
          </div>
          <div className={s.sortActions}>
            <PillTabs
              tabs={SORT_TABS
                .filter(tab => (!tab.authOnly || isAuthenticated) && (tab.key !== 'pinned' || PINNING_ENABLED))
                .map(tab => ({ key: tab.key, label: tab.label }))
              }
              active={activeSort}
              onChange={(key) => {
                if (!isAuthenticated) window.dispatchEvent(new Event(DOM_EVENTS.guestInteraction))
                setActiveSort(key)
                setCursor(undefined)
              }}
              loading={activeFetching}
            />
            <RefreshButton onClick={() => { setCursor(undefined); void activeRefetch() }} loading={activeFetching} style={{ cursor: activeFetching ? 'wait' : 'pointer' }} />
          </div>
        </div>
      </div>

      <div className={s.scrollList}>

        {(categorySlugs.length > 0 || tagFilter) && (
          <div className={s.filterChips}>
            {categorySlugs.map(slug => {
              const cat = allCategories.find(c => c.slug === slug)
              return (
                <FilterChip
                  key={slug}
                  label={`${cat?.icon ? cat.icon + ' ' : ''}${cat?.name ?? slug}`}
                  onRemove={() => handleCategoryChange(categorySlugs.filter(s => s !== slug))}
                />
              )
            })}
            {tagFilter && (
              <FilterChip label={`#${tagFilter}`} onRemove={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('tag'); return n }, { replace: true })} />
            )}
          </div>
        )}

        {/* Skeletons */}
        {activeLoading && <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>}

        {/* Empty state */}
        {!activeLoading && !discussions.length && !activeFetching && (
          <EmptyState
            icon={activeSort === 'pinned' ? '📌' : activeSort === 'foryou' ? '✨' : '💬'}
            title={activeSort === 'pinned' ? 'No pinned discussions' : activeSort === 'foryou' ? 'Your feed is getting ready' : 'No discussions yet'}
            description={activeSort === 'pinned' ? 'Pin your own discussions to feature them here' : activeSort === 'foryou' ? 'Start posting and following topics to personalise this feed' : 'Be the first to start a discussion!'}
          />
        )}

        {/* For You — non-personalised hint */}
        {isForYou && !activeLoading && discussions.length > 0 && !(activeData as any)?.isPersonalized && (
          <Alert variant="info" style={{ marginBottom: '0.75rem' }}>
            Showing trending discussions.{' '}
            <Link to="/settings" style={{ color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: 500 }}>
              Set your interests
            </Link>{' '}
            to personalise this feed.
          </Alert>
        )}

        {/* For You — personalised info */}
        {isForYou && !activeLoading && discussions.length > 0 && (activeData as any)?.isPersonalized && (
          <Alert variant="info" style={{ marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <span>✨ Personalised from followed threads, your interests, and people you follow.</span>
            {(activeData as any)?.emptyInterests?.length > 0 && (
              <span style={{ color: 'var(--text-muted)' }}>
                {' '}No posts yet in{' '}
                <strong style={{ color: 'var(--text-primary)' }}>
                  {((activeData as any).emptyInterests as string[])
                    .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '))
                    .join(', ')}
                </strong>.
              </span>
            )}
            <Link to="/settings" style={{ color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: 500, marginLeft: 'auto', flexShrink: 0, paddingLeft: '0.35rem' }}>
              Edit interests →
            </Link>
          </Alert>
        )}

        {/* Discussion list */}
        {discussions.map((d) => (
          <div key={d.id} onClick={() => { if (!isAuthenticated) window.dispatchEvent(new Event(DOM_EVENTS.guestInteraction)) }}>
            <DiscussionCard
              id={d.id}
              title={d.title}
              contentPreview={contentPreview(d.content)}
              author={{ id: d.author.id, displayName: d.author.profile?.displayName ?? 'User', avatarUrl: d.author.profile?.avatarUrl, flair: d.author.profile?.flair }}
              categories={d.categories}
              tags={d.tags.map(t => t.tag.name)}
              viewCount={d.viewCount}
              commentCount={d.commentCount}
              voteScore={d.voteScore}
              createdAt={d.createdAt}
              isPinned={Array.isArray(d.pins) ? d.pins.length > 0 : false}
            />
          </div>
        ))}

        <SentinelLoader ref={loaderRef} isFetching={activeFetching && !activeLoading} />

      </div>

      {/* Guest CTA — pinned outside the scroll list so it stays fixed at the bottom */}
      {!isAuthenticated && !isLoading && discussions.length > 0 && (
        <div className={s.guestCta}>
          <div className={s.guestCtaText}>
            <div className={s.guestCtaTitle}>Want AI-powered answers?</div>
            <div className={s.guestCtaSub}>Sign in to ask questions, comment, and get answers from specialist AI agents</div>
          </div>
          <Button size="sm" onClick={() => promptLogin('Sign in to ask questions, comment, and get AI-powered answers')} style={{ whiteSpace: 'nowrap' }}>
            Sign in free
          </Button>
        </div>
      )}
    </div>
  )
}
